import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, Send, Trash2, Copy, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const INVITE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

const StatusPill = ({ status }) => {
    const map = {
        active: { label: "Active", cls: "bg-emerald-100 text-emerald-800" },
        pending: { label: "Pending", cls: "bg-amber-100 text-amber-800" },
        revoked: { label: "Revoked", cls: "bg-neutral-200 text-neutral-700" },
    };
    const s = map[status] || map.pending;
    return (
        <span data-testid={`seat-status-${status}`} className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
            {s.label}
        </span>
    );
};

export default function SeatManager() {
    const { user, refresh } = useAuth();
    const [loading, setLoading] = useState(true);
    const [license, setLicense] = useState(null);
    const [state, setState] = useState({ seat_limit: 50, used: 0, pending: 0, available: 50, seats: [] });
    const [emailsRaw, setEmailsRaw] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [copiedToken, setCopiedToken] = useState(null);
    const [notLicensed, setNotLicensed] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const mine = await api.get("/license/mine");
            if (!mine.data?.license) {
                setNotLicensed(true);
                setLoading(false);
                return;
            }
            setLicense(mine.data.license);
            const seats = await api.get("/license/seats");
            setState(seats.data);
            setNotLicensed(false);
        } catch (e) {
            if (e?.response?.status === 403) setNotLicensed(true);
            else toast.error("Could not load seat information");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const parsedEmails = useMemo(() => {
        return emailsRaw
            .split(/[\s,;]+/)
            .map((e) => e.trim().toLowerCase())
            .filter((e) => e && e.includes("@"));
    }, [emailsRaw]);

    const handleInvite = async (e) => {
        e.preventDefault();
        if (parsedEmails.length === 0) {
            toast.error("Enter at least one email address");
            return;
        }
        if (parsedEmails.length > state.available) {
            toast.error(`Only ${state.available} seat${state.available === 1 ? "" : "s"} remaining. Reduce the list.`);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/license/seats/invite", { emails: parsedEmails });
            const created = data.created?.length || 0;
            const rejected = data.rejected?.length || 0;
            if (created) toast.success(`Sent ${created} invite${created === 1 ? "" : "s"}`);
            if (rejected) toast.message(`${rejected} skipped: ${data.rejected.map((r) => `${r.email} (${r.reason})`).join(", ")}`);
            setEmailsRaw("");
            await loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invite failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleRevoke = async (seat) => {
        if (!window.confirm(`Revoke seat for ${seat.email}? They will lose module access immediately.`)) return;
        try {
            await api.post("/license/seats/revoke", { seat_id: seat.id });
            toast.success(`Revoked ${seat.email}`);
            await loadAll();
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Revoke failed");
        }
    };

    const copyInviteLink = async (seat) => {
        const link = `${INVITE_ORIGIN}/invite/${seat.invite_token}`;
        try {
            await navigator.clipboard.writeText(link);
            setCopiedToken(seat.id);
            toast.success("Invite link copied");
            setTimeout(() => setCopiedToken(null), 2000);
        } catch {
            toast.error("Copy failed — select the URL manually");
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-neutral-500" data-testid="seat-loading">Loading…</div>;
    }

    if (notLicensed) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-24" data-testid="seat-not-licensed">
                <div className="flex items-start gap-4 p-6 border border-amber-300 bg-amber-50 rounded-lg">
                    <AlertTriangle className="text-amber-600 mt-1" size={22} />
                    <div>
                        <h1 className="text-xl font-bold text-neutral-900 mb-2">No School Site License on this account</h1>
                        <p className="text-sm text-neutral-700 mb-4">
                            The seat manager is available only to purchasers of the School Site License. If you recently bought one and see this
                            message, contact <a href="mailto:gdobson@degreesofsuccessinfo.com" className="underline">gdobson@degreesofsuccessinfo.com</a> with your receipt.
                        </p>
                        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900" data-testid="seat-back-dashboard">
                            <ArrowLeft size={16} /> Back to dashboard
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const used = state.used;
    const pending = state.pending;
    const available = state.available;
    const seatLimit = state.seat_limit;
    const barPct = Math.min(100, Math.round(((used + pending) / seatLimit) * 100));

    return (
        <div className="max-w-5xl mx-auto px-6 py-12" data-testid="seat-manager-page">
            <div className="mb-6">
                <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900" data-testid="seat-back-dashboard">
                    <ArrowLeft size={14} /> Back to dashboard
                </Link>
            </div>

            <header className="mb-8">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-blue-700 font-semibold mb-3">
                    <Users size={14} /> School Site License
                </div>
                <h1 className="text-4xl sm:text-5xl font-black text-neutral-900 leading-tight">Manage your seats</h1>
                <p className="text-neutral-600 mt-2 max-w-2xl">
                    Invite up to <strong>{seatLimit}</strong> faculty. Each accepted seat unlocks all six modules. Revoking a seat removes access immediately.
                </p>
            </header>

            {/* Usage bar */}
            <section className="p-6 border border-neutral-200 rounded-xl bg-white mb-8" data-testid="seat-usage-card">
                <div className="flex items-baseline justify-between mb-3">
                    <div>
                        <div className="text-xs text-neutral-500 uppercase tracking-widest">Seats used</div>
                        <div className="text-3xl font-black text-neutral-900" data-testid="seat-count-used">
                            {used + pending}
                            <span className="text-neutral-400 font-medium"> / {seatLimit}</span>
                        </div>
                    </div>
                    <div className="text-right text-sm">
                        <div><span className="text-emerald-700 font-semibold" data-testid="seat-count-active">{used}</span> active</div>
                        <div><span className="text-amber-700 font-semibold" data-testid="seat-count-pending">{pending}</span> pending</div>
                        <div><span className="text-neutral-700 font-semibold" data-testid="seat-count-available">{available}</span> available</div>
                    </div>
                </div>
                <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
                    <div className="h-full bg-blue-700" style={{ width: `${barPct}%` }} />
                </div>
            </section>

            {/* Invite form */}
            <section className="p-6 border border-neutral-200 rounded-xl bg-white mb-8" data-testid="seat-invite-card">
                <h2 className="text-lg font-bold text-neutral-900 mb-1">Invite faculty</h2>
                <p className="text-sm text-neutral-500 mb-4">Paste one or many emails (comma, space, or newline separated). Invitees receive an invite link.</p>
                <form onSubmit={handleInvite} className="space-y-3">
                    <textarea
                        data-testid="seat-invite-textarea"
                        value={emailsRaw}
                        onChange={(e) => setEmailsRaw(e.target.value)}
                        placeholder="faculty1@school.edu, faculty2@school.edu&#10;faculty3@school.edu"
                        rows={4}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />
                    <div className="flex items-center justify-between">
                        <div className="text-xs text-neutral-500" data-testid="seat-invite-preview">
                            {parsedEmails.length > 0
                                ? `${parsedEmails.length} email${parsedEmails.length === 1 ? "" : "s"} ready · ${available} seat${available === 1 ? "" : "s"} remaining`
                                : `${available} seat${available === 1 ? "" : "s"} remaining`}
                        </div>
                        <button
                            type="submit"
                            disabled={submitting || parsedEmails.length === 0 || parsedEmails.length > available}
                            data-testid="seat-invite-submit"
                            className="inline-flex items-center gap-2 bg-blue-700 text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Send size={14} /> {submitting ? "Sending…" : `Send ${parsedEmails.length || ""} invite${parsedEmails.length === 1 ? "" : "s"}`.trim()}
                        </button>
                    </div>
                </form>
            </section>

            {/* Seat list */}
            <section className="border border-neutral-200 rounded-xl bg-white overflow-hidden" data-testid="seat-list-card">
                <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
                    <h2 className="text-lg font-bold text-neutral-900">Seats</h2>
                    <span className="text-xs text-neutral-500">{state.seats.length} on this license</span>
                </div>
                {state.seats.length === 0 ? (
                    <div className="px-6 py-12 text-center text-neutral-500" data-testid="seat-list-empty">
                        No seats yet. Invite your first faculty above.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="seat-list-table">
                        <thead className="bg-neutral-50 text-left text-xs uppercase tracking-widest text-neutral-500">
                            <tr>
                                <th className="px-6 py-3">Email</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3">Invited</th>
                                <th className="px-6 py-3">Accepted</th>
                                <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.seats.map((seat) => (
                                <tr key={seat.id} className="border-t border-neutral-100" data-testid={`seat-row-${seat.email}`}>
                                    <td className="px-6 py-3 font-medium text-neutral-900">{seat.email}</td>
                                    <td className="px-6 py-3"><StatusPill status={seat.status} /></td>
                                    <td className="px-6 py-3 text-neutral-500">{seat.invited_at ? new Date(seat.invited_at).toLocaleDateString() : "—"}</td>
                                    <td className="px-6 py-3 text-neutral-500">{seat.accepted_at ? new Date(seat.accepted_at).toLocaleDateString() : "—"}</td>
                                    <td className="px-6 py-3 text-right">
                                        <div className="inline-flex items-center gap-2">
                                            {seat.status === "pending" && (
                                                <button
                                                    onClick={() => copyInviteLink(seat)}
                                                    data-testid={`seat-copy-link-${seat.email}`}
                                                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
                                                    title="Copy invite link"
                                                >
                                                    {copiedToken === seat.id ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                                                    {copiedToken === seat.id ? "Copied" : "Copy link"}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleRevoke(seat)}
                                                data-testid={`seat-revoke-${seat.email}`}
                                                className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900"
                                                title="Revoke seat"
                                            >
                                                <Trash2 size={14} /> Revoke
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <p className="text-xs text-neutral-500 mt-8" data-testid="seat-owner-note">
                Logged in as {user?.email}. Invite links are one-time. Recipients must sign in with the invited email to accept.
            </p>
        </div>
    );
}
